<template>
    <div class="form-group" :class="model.size">
        <label class="font-weight-bold" :class="labelClasses" :for="'input' + _uid">
            {{ isEnVersion ? model.labelEn : model.label }}
        </label>
        <textarea v-if="model.inputType === 'textarea'"
                  class="form-control"
                  :class="model.fieldCode"
                  v-model="initialValue"
                  :id="'input' + _uid"
                  :name="'input' + _uid"
                  rows="5"
                  :data-vv-as="model.label"
                  v-validate="validations"
                  @input="inputChanged"
                  :readonly="model.isReadOnly || details"
                  aria-describedby="descriptionHelp"></textarea>
        <input v-else
               class="form-control"
               :class="model.fieldCode"
               :type="model.inputType"
               v-model="initialValue"
               :id="'input' + _uid"
               :name="'input' + _uid"
               :data-vv-as="model.label"
               v-validate="validations"
               @input="inputChanged"
               :readonly="model.isReadOnly || details"
               aria-describedby="descriptionHelp">
        <div class="text-danger" v-if="errors.has('input' + _uid)">{{ errors.first('input' + _uid) }}</div>
        <small id="descriptionHelp" class="form-text text-muted">{{ isEnVersion ? model.descriptionEn : model.description }}</small>
    </div>
</template>

<script>
    import { InputType, NumberType } from '../../models/enums';
    import { EditorComponentModel } from '../../models/models';
    import { mapGetters } from 'vuex';
    import { RegisterEventBus } from '@/buses/register-bus.js';

    export default {
        inject: ['$validator'],
        props: {
            model: {
                type: EditorComponentModel
            },
            value: [String, Number],
            details: {
                type: Boolean,
                default: false
            },
            allowAnonymous: {
                type: Boolean,
                default: true
            },
            profileData: {
                type: Object,
                default() {
                    return null;
                }
            },
            isEvaluationPanel: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                validations: '',
                initialValue: '',
                labelClasses: ''
            }
        },
        methods: {
            inputChanged(event) {
                if (this.model.inputType === InputType.Text || this.model.inputType === InputType.TextArea) {
                    this.model.fieldValue.textValue = event.target.value;
                } else {
                    if (this.model.numberType === NumberType.Int) {
                        this.model.fieldValue.intValue = event.target.value;
                    } else {
                        this.model.fieldValue.decimalValue = event.target.value;
                    }
                }
                this.$emit('input', event.target.value);
                RegisterEventBus.$emit('dynamic-field-change', event.target.value, event.target.value, this.model);
                this.model.fieldValue.stringValue = event.target.value;
            },
            addValidations() {
                if (this.model.isRequired) {
                    this.addValidation('required');
                    this.labelClasses += 'required';
                }

                if (this.model.inputType === InputType.Text || this.model.inputType === InputType.TextArea) {
                    this.addTextValidations();
                } else {
                    this.addNumberValidations();
                }
            },
            addValidation(validation) {
                if (this.validations.length > 0) {
                    this.validations += '|';
                }
                this.validations += validation;
            },
            addTextValidations() {
                if (this.model.textMinLength && !isNaN(parseInt(this.model.textMinLength))) {
                    this.addValidation(`min:${this.model.textMinLength}`);
                }
                if (this.model.textMaxLength && !isNaN(parseInt(this.model.textMaxLength))) {
                    this.addValidation(`max:${this.model.textMaxLength}`);
                }
            },
            addNumberValidations() {
                if (this.model.numberType === NumberType.Int) {
                    this.addIntValidations();
                } else {
                    this.addDecimalValidations();
                }
            },
            addIntValidations() {
                if (this.model.intMinValue && !isNaN(parseInt(this.model.intMinValue))) {
                    this.addValidation(`min_value:${this.model.intMinValue}`);
                }
                if (this.model.intMaxValue && !isNaN(parseInt(this.model.intMaxValue))) {
                    this.addValidation(`max_value:${this.model.intMaxValue}`);
                }
            },
            addDecimalValidations() {
                if (this.model.decimalPrecision) {
                    this.addValidation(`decimal:${this.model.decimalPrecision}`);
                }
                if (!isNaN(parseInt(this.model.decimalMinValue))) {
                    this.addValidation(`min_value:${this.model.decimalMinValue}`);
                }
                if (this.model.decimalMaxValue && !isNaN(parseInt(this.model.decimalMaxValue))) {
                    this.addValidation(`max_value:${this.model.decimalMaxValue}`);
                }
            },
            loadInitialValue() {
                if (this.model.inputType === InputType.Text || this.model.inputType === InputType.TextArea) {
                    this.initialValue = this.model.fieldValue.textValue ? this.model.fieldValue.textValue : this.model.textDefaultValue;
                    this.model.fieldValue.textValue = this.initialValue;
                    this.model.fieldValue.stringValue = this.initialValue;
                } else {
                    if (this.model.numberType === NumberType.Int) {
                        this.initialValue = this.model.fieldValue.intValue ?
                            this.model.fieldValue.intValue : this.model.intDefaultValue;
                        this.model.fieldValue.intValue = this.initialValue;
                        this.model.fieldValue.stringValue = this.initialValue;
                    } else {
                        this.initialValue = this.model.fieldValue.decimalValue ?
                            this.model.fieldValue.decimalValue : this.model.decimalDefaultValue;
                        this.model.fieldValue.decimalValue = this.initialValue;
                        this.model.fieldValue.stringValue = this.initialValue;
                    }
                }
            },
            fillProfileData(data) {
                if (!data) {
                    return;
                }

                const value = this.$helpers.fillProfileDataToDynamicForm(this.model, data);

                if (!value) {
                    this.$emit('input', value);
                }
            }
        },
        mounted() {
            this.addValidations();
            this.loadInitialValue();

            this.model.uid = this._uid;
            this.model.isEvaluationPanel = this.isEvaluationPanel;
        },
        computed: {
            ...mapGetters(['isEnVersion']),
        },
        watch: {
            'model.fieldValue.textValue': function(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.initialValue = newVal;
                }
            },
            profileData: {
                handler: function (newVal, oldVal) {
                    this.fillProfileData(newVal);
                },
                deep: true,
                immediate: true,
            },
        }
    }
</script>
